import { toast } from 'sonner';
import { api } from '@/redux/api';
import { withToast } from '@/redux/api/withToast';
import { QueryReturnValue } from '@/redux/types/query-return-value';
import {
  type DeviceAssociatedWithUnit,
  type DeviceUnassociated,
  MaybeAssociatedDevice,
} from '@/types/device';

import {
  AddDeviceToOrganisationQueryArg,
  addDeviceToOrganisationQueryBody,
  handleResultAddDeviceToOrganisation,
} from './add-device-to-organisation';
import addToOrganisationStrings from './add-device-to-organisation/strings';

import {
  AddDeviceToUnitQueryArg,
  addDeviceToUnitQueryBody,
  handleResultAddDeviceToUnit,
} from './add-device-to-unit';

import {
  DeleteDeviceQueryArg,
  handleResultDeleteDevice,
} from './delete-device';
import deleteDeviceStrings from './delete-device/strings';

import {
  GetAssociatedDevicesInOrganisationQueryArg,
  getAssociatedDevicesInOrganisationQueryParams,
} from './get-associated-devices-in-organisation';

import {
  GetAssociatedDevicesInUnitQueryArg,
  getAssociatedDevicesInUnitQueryParams,
} from './get-associated-devices-in-unit';

import { GetUnassociatedDevicesQueryArg } from './get-unassociated-devices';

import {
  handleResultRemoveDeviceFromOrganisation,
  RemoveDeviceFromOrganisationQueryArg,
  removeDeviceFromOrganisationQueryBody,
} from './remove-device-from-organisation';
import removeFromOrganisationStrings from './remove-device-from-organisation/strings';

import {
  handleResultRemoveDeviceFromUnit,
  RemoveDeviceFromUnitQueryArg,
  removeDeviceFromUnitQueryBody,
  strings as removeFromUnitStrings,
} from './remove-device-from-unit';

/**
 * Add and remove devices from organisations and units, plus narrow
 * device queries down to organisation and unit level.
 */
export const deviceApi = api.injectEndpoints({
  endpoints: (builder) => ({
    addDeviceToOrganisation: builder.mutation<
      void,
      AddDeviceToOrganisationQueryArg
    >({
      query: (queryArg) => ({
        url: '/device',
        method: 'PATCH',
        body: addDeviceToOrganisationQueryBody(queryArg),
      }),
      onQueryStarted: withToast(addToOrganisationStrings)(
        handleResultAddDeviceToOrganisation,
      ),
      invalidatesTags: ['UnimportedDevices', 'Devices'],
    }),
    removeDeviceFromOrganisation: builder.mutation<
      void,
      RemoveDeviceFromOrganisationQueryArg
    >({
      query: (queryArg) => ({
        url: '/device',
        method: 'PATCH',
        body: removeDeviceFromOrganisationQueryBody(queryArg),
      }),
      onQueryStarted: withToast(removeFromOrganisationStrings)(
        handleResultRemoveDeviceFromOrganisation,
      ),
      invalidatesTags: ['Devices'],
    }),
    getUnassociatedDevices: builder.query<
      DeviceUnassociated[],
      GetUnassociatedDevicesQueryArg
    >({
      query: (queryArg) => ({
        url: '/device',
        method: 'GET',
        params: {
          unitId: 'UNASSOCIATED',
          ...(queryArg.organisationId
            ? { organisationId: queryArg.organisationId }
            : {}),
        },
      }),
      providesTags: ['Devices'],
    }),
    getDevicesInOrganisation: builder.query<
      MaybeAssociatedDevice[],
      GetAssociatedDevicesInOrganisationQueryArg // TODO: Make own type
    >({
      serializeQueryArgs: ({ queryArgs }) => queryArgs.organisation.id,
      query: (queryArg) => ({
        url: '/device',
        method: 'GET',
        params: {
          organisationId: queryArg.organisation.id,
          unitId: 'ALL',
        },
      }),
      providesTags: ['Devices'],
    }),
    getAssociatedDevicesInOrganisation: builder.query<
      DeviceAssociatedWithUnit[],
      GetAssociatedDevicesInOrganisationQueryArg
    >({
      serializeQueryArgs: ({ queryArgs }) => queryArgs.organisation.id,
      query: (queryArg) => ({
        url: '/device',
        params: getAssociatedDevicesInOrganisationQueryParams(queryArg),
      }),
      providesTags: ['Devices'],
    }),
    getAssociatedDevicesInUnit: builder.query<
      DeviceAssociatedWithUnit[],
      GetAssociatedDevicesInUnitQueryArg
    >({
      serializeQueryArgs: ({ queryArgs }) => queryArgs.unit.id,
      query: (queryArg) => ({
        url: '/device',
        params: getAssociatedDevicesInUnitQueryParams(queryArg),
      }),
      providesTags: ['Devices'],
    }),
    addDeviceToUnit: builder.mutation<void | string, AddDeviceToUnitQueryArg>({
      queryFn: async (queryArg, _api, _extraOptions, baseQuery) => {
        // Is the device already in the unit? Skip the network call
        if (queryArg.device?.unit?.id === queryArg.unitId) {
          return {} as QueryReturnValue<void>;
        }
        const result = await baseQuery({
          url: '/device',
          method: 'PATCH',
          body: addDeviceToUnitQueryBody(queryArg),
        });
        return result as QueryReturnValue<string>;
      },
      onQueryStarted: async (queryArg, api) => {
        // Is the device already in the unit? Skip any mutations
        if (queryArg.device?.unit?.id === queryArg.unitId) {
          return;
        }
        const toastId = toast.loading('Adding device to unit...');
        try {
          await handleResultAddDeviceToUnit(queryArg, api);
          toast.success('Added device to unit', { id: toastId });
        } catch {
          toast.error('Failed to add device to unit', { id: toastId });
        }
      },
      invalidatesTags: ['Devices'],
    }),
    removeDeviceFromUnit: builder.mutation<
      void | string,
      RemoveDeviceFromUnitQueryArg
    >({
      query: (queryArg) => ({
        url: '/device',
        method: 'PATCH',
        body: removeDeviceFromUnitQueryBody(queryArg),
      }),
      onQueryStarted: withToast(removeFromUnitStrings)(
        handleResultRemoveDeviceFromUnit,
      ),
      invalidatesTags: ['Devices'],
    }),
    deleteDevice: builder.mutation<void, DeleteDeviceQueryArg>({
      query: (queryArg) => ({
        url: '/device',
        method: 'DELETE',
        params: queryArg,
      }),
      onQueryStarted: withToast(deleteDeviceStrings)(handleResultDeleteDevice),
      invalidatesTags: ['Devices'],
    }),
  }),
});

export const {
  useAddDeviceToOrganisationMutation,
  useRemoveDeviceFromOrganisationMutation,

  useGetUnassociatedDevicesQuery,
  useGetAssociatedDevicesInUnitQuery,
  useAddDeviceToUnitMutation,
  useRemoveDeviceFromUnitMutation,
  useGetDevicesInOrganisationQuery,
  useDeleteDeviceMutation,
} = deviceApi;
